import { useUserData } from '@nhost/react';
import { useCallback } from 'react';
import { validate } from 'uuid';
import {
  useUpsertThreadMutation,
  useRemoveCommentMutation,
  useThreadSubscription,
  Comment,
  GetEsrsMetricAnswerDocument_,
  User,
  AttachmentBox_Constraint_,
  AttachmentBox_Update_Column_,
  Esrs_Answer_Constraint_,
  Esrs_Answer_Update_Column_,
  Esrs_DatapointTag_Constraint_,
  GetEsrsMetricsDatapointsDocument_,
  NoteHistory_Constraint_,
  NoteHistory_Update_Column_,
  useUpsertDatapointMutation,
  GetSingleEsrsMetricAnswerDocument_,
  EsrsAssessmentAnswersAggregateDocument_,
} from 'models';
import { useCurrentCompanyId } from 'utils/hooks';
import { TimePeriods } from '../../Requirement';
import { MetricsTableData } from '../MetricAnswers.hooks';
import { useParams } from 'react-router-dom';

export function useMetricsCommentThread({
  id,
  esrsDatapointId,
  timeframe,
  assessmentProjectLeaderId,
  row,
  companyReportingUnit,
}: {
  id: string;
  esrsDatapointId: string;
  timeframe: TimePeriods;
  assessmentProjectLeaderId?: Partial<User>;
  row: MetricsTableData;
  companyReportingUnit?: string;
}) {
  const user = useUserData();
  const { esrsAssessmentId = '' } = useParams();
  const { companyId } = useCurrentCompanyId();
  const [upsertThread] = useUpsertThreadMutation();
  const [deleteCommentMutation] = useRemoveCommentMutation();
  const { data: threadResult, loading } = useThreadSubscription({
    skip: !validate(id),
    variables: { id },
  });
  const [upsertDatapoint] = useUpsertDatapointMutation();
  const thread = threadResult?.thread;
  const addComment = useCallback(
    (comment: string) => {
      if (!esrsDatapointId) {
        return upsertDatapoint({
          variables: {
            objects: {
              value: null,
              timeframe: timeframe,
              authorId: user?.id,
              ownerId: assessmentProjectLeaderId?.id,
              source: null,
              datapointTags: {
                data: row?.tags ?? [],
                on_conflict: {
                  constraint:
                    Esrs_DatapointTag_Constraint_.DatapointTagDatapointIdTagValueTagTypeKey_,
                },
              },
              answer: {
                data: {
                  metricRef: row.metric.reference,
                  reportingUnitId: companyReportingUnit,
                  assessmentId: esrsAssessmentId,
                  attachmentBox: {
                    data: {},
                    on_conflict: {
                      constraint: AttachmentBox_Constraint_.AttachmentBoxMetricAnswerIdKey1_,
                      update_columns: [AttachmentBox_Update_Column_.MetricAnswerId_],
                    },
                  },
                  noteHistory: {
                    data: {},
                    on_conflict: {
                      constraint: NoteHistory_Constraint_.NoteHistoryMetricAnswerIdKey_,
                      update_columns: [NoteHistory_Update_Column_.MetricAnswerId_],
                    },
                  },
                  optOutAuthorId: user?.id,
                },
                on_conflict: {
                  constraint:
                    Esrs_Answer_Constraint_.AnswerMetricRefAssessmentIdReportingUnitIdKey_,
                  update_columns: [
                    Esrs_Answer_Update_Column_.HasOptedOut_,
                    Esrs_Answer_Update_Column_.OptOutReason_,
                    Esrs_Answer_Update_Column_.OptOutAuthorId_,
                  ],
                },
              },
            },
          },

          refetchQueries: [
            GetEsrsMetricAnswerDocument_,
            GetEsrsMetricsDatapointsDocument_,
            GetSingleEsrsMetricAnswerDocument_,
            EsrsAssessmentAnswersAggregateDocument_,
          ],
        }).then((res) => {
          const dpId: string = res.data?.insert_esrs_Datapoint?.returning[0].id;
          const threadId: string = res.data?.insert_esrs_Datapoint?.returning[0].thread?.id;
          if (dpId)
            upsertThread({
              variables: {
                thread: {
                  id: threadId,
                  companyId,
                  esrsDatapointId: dpId,
                  comments: {
                    data: [
                      {
                        data: comment,
                        authorId: user?.id,
                      },
                    ],
                  },
                  subscribers: undefined,
                },
              },
              refetchQueries: [GetEsrsMetricAnswerDocument_],
            });
        });
      }
      return upsertThread({
        variables: {
          thread: {
            id,
            companyId,
            esrsDatapointId,
            comments: {
              data: [
                {
                  data: comment,
                  authorId: user?.id,
                },
              ],
            },
            subscribers: undefined,
          },
        },
        refetchQueries: [GetEsrsMetricAnswerDocument_],
      });
    },
    [upsertThread, thread]
  );

  const deleteComment = useCallback(
    (commentId: Comment['id']) => deleteCommentMutation({ variables: { id: commentId } }),
    [deleteCommentMutation]
  );

  return { addComment, deleteComment, thread: threadResult?.thread, loading };
}
